/**
 * Dashboard analytics helpers.
 * All functions operate on the normalised order array returned by loadOrders().
 */

import { getOrderLifecycleKey } from "./orders";

/* ── helpers ──────────────────────────────────────────────── */

const parseDate = (value) => {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

const startOfDay = (d) => {
  const r = new Date(d);
  r.setHours(0, 0, 0, 0);
  return r;
};

const dayKey = (d) => d.toISOString().slice(0, 10);

const isDelivered = (o) => getOrderLifecycleKey(o) === "delivered";
const isCancelled = (o) => getOrderLifecycleKey(o) === "cancelled";
const isPaid = (o) => o.paymentStatus === "Payee";

/* ── revenue by day ───────────────────────────────────────── */

export const revenueByDay = (orders, days = 30) => {
  const now = startOfDay(new Date());
  const map = {};

  for (let i = 0; i < days; i++) {
    const d = new Date(now);
    d.setDate(d.getDate() - i);
    map[dayKey(d)] = { date: dayKey(d), revenue: 0, orders: 0 };
  }

  for (const o of orders) {
    const d = parseDate(o.date);
    if (!d) continue;
    const key = dayKey(startOfDay(d));
    if (map[key]) {
      map[key].revenue += Number(o.amount || 0);
      map[key].orders += 1;
    }
  }

  return Object.values(map).reverse();
};

/* ── revenue by week (last N weeks) ──────────────────────── */

export const revenueByWeek = (orders, weeks = 12) => {
  const now = startOfDay(new Date());
  const map = {};

  for (let i = 0; i < weeks; i++) {
    const weekStart = new Date(now);
    weekStart.setDate(weekStart.getDate() - weekStart.getDay() + 1 - i * 7);
    const key = dayKey(weekStart);
    map[key] = {
      weekStart: key,
      label: `S${Math.ceil((weekStart.getDate()) / 7)}`,
      revenue: 0,
      orders: 0,
    };
  }

  for (const o of orders) {
    const d = parseDate(o.date);
    if (!d) continue;
    const weekStart = new Date(d);
    weekStart.setDate(weekStart.getDate() - weekStart.getDay() + 1);
    const key = dayKey(startOfDay(weekStart));
    if (map[key]) {
      map[key].revenue += Number(o.amount || 0);
      map[key].orders += 1;
    }
  }

  return Object.values(map).reverse();
};

/* ── revenue by month (last N months) ────────────────────── */

export const revenueByMonth = (orders, months = 12) => {
  const now = new Date();
  const map = {};

  for (let i = 0; i < months; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    map[key] = {
      month: key,
      label: d.toLocaleDateString("fr-FR", { month: "short", year: "2-digit" }),
      revenue: 0,
      orders: 0,
    };
  }

  for (const o of orders) {
    const d = parseDate(o.date);
    if (!d) continue;
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    if (map[key]) {
      map[key].revenue += Number(o.amount || 0);
      map[key].orders += 1;
    }
  }

  return Object.values(map).reverse();
};

/* ── busiest hours (orders per hour 0-23) ────────────────── */

export const busiestHours = (orders, days = 30) => {
  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - days);
  const hours = Array.from({ length: 24 }, (_, i) => ({ hour: i, orders: 0, revenue: 0 }));

  for (const o of orders) {
    const d = parseDate(o.date);
    if (!d || d < cutoff) continue;
    hours[d.getHours()].orders += 1;
    hours[d.getHours()].revenue += Number(o.amount || 0);
  }

  return hours;
};

/* ── top selling products ────────────────────────────────── */

export const topSellingProducts = (orders, limit = 10) => {
  const map = {};

  for (const o of orders) {
    if (!isPaid(o) && !isDelivered(o)) continue;
    for (const item of o.items || []) {
      const key = item.productId || item.name;
      if (!map[key]) {
        map[key] = {
          productId: item.productId,
          name: item.name || "Produit",
          quantity: 0,
          revenue: 0,
          orders: 0,
        };
      }
      map[key].quantity += Number(item.quantity || 0);
      map[key].revenue += Number(item.lineTotal || 0);
      map[key].orders += 1;
    }
  }

  return Object.values(map)
    .sort((a, b) => b.revenue - a.revenue)
    .slice(0, limit);
};

/* ── payment method breakdown ────────────────────────────── */

export const paymentMethodBreakdown = (orders) => {
  const map = {};

  for (const o of orders) {
    if (!isPaid(o)) continue;
    const method = o.paymentMethod || "Non specifie";
    if (!map[method]) map[method] = { method, count: 0, revenue: 0 };
    map[method].count += 1;
    map[method].revenue += Number(o.amount || 0);
  }

  return Object.values(map).sort((a, b) => b.revenue - a.revenue);
};

/* ── order status breakdown ──────────────────────────────── */

export const orderStatusBreakdown = (orders) => {
  const map = {};

  for (const o of orders) {
    const key = getOrderLifecycleKey(o);
    if (!map[key]) map[key] = { status: key, count: 0 };
    map[key].count += 1;
  }

  return Object.values(map).sort((a, b) => b.count - a.count);
};

/* ── summary metrics ─────────────────────────────────────── */

export const computeDashboardMetrics = (orders, products) => {
  const now = new Date();
  const todayKey = dayKey(startOfDay(now));
  const yesterdayDate = new Date(now);
  yesterdayDate.setDate(yesterdayDate.getDate() - 1);
  const yesterdayKey = dayKey(startOfDay(yesterdayDate));

  const paidOrders = orders.filter(isPaid);
  const deliveredOrders = orders.filter(isDelivered);
  const cancelledOrders = orders.filter(isCancelled);

  const todayOrders = orders.filter((o) => {
    const d = parseDate(o.date);
    return d && dayKey(startOfDay(d)) === todayKey;
  });
  const yesterdayOrders = orders.filter((o) => {
    const d = parseDate(o.date);
    return d && dayKey(startOfDay(d)) === yesterdayKey;
  });

  const todayRevenue = todayOrders.reduce((s, o) => s + Number(o.amount || 0), 0);
  const yesterdayRevenue = yesterdayOrders.reduce((s, o) => s + Number(o.amount || 0), 0);

  const totalRevenue = paidOrders.reduce((s, o) => s + Number(o.amount || 0), 0);
  const avgOrderValue = paidOrders.length ? totalRevenue / paidOrders.length : 0;

  const activeOrders = orders.filter(
    (o) => !["delivered", "cancelled"].includes(getOrderLifecycleKey(o))
  ).length;

  const lowStockCount = products.filter((p) => p.stock <= 10).length;

  const revenueTrend = yesterdayRevenue > 0
    ? ((todayRevenue - yesterdayRevenue) / yesterdayRevenue) * 100
    : todayRevenue > 0 ? 100 : 0;

  return {
    todayRevenue,
    yesterdayRevenue,
    totalRevenue,
    avgOrderValue,
    todayOrders: todayOrders.length,
    yesterdayOrders: yesterdayOrders.length,
    totalOrders: orders.length,
    paidOrders: paidOrders.length,
    deliveredOrders: deliveredOrders.length,
    cancelledOrders: cancelledOrders.length,
    activeOrders,
    lowStockCount,
    revenueTrend,
    totalProducts: products.length,
  };
};
